import { parseFormattedNumber } from '@/features/order/lib/number-format-util'
import {
  resolveDenomination,
  resolveEffectiveAmount,
} from '@/features/order/order-form/lib/instrument-resolution'
import {
  AnchorPrice,
  computeEstimatedPrice,
  OffsetUnit,
  PriceAnchor,
} from '@/features/order/order-form/lib/resolve-anchor-price'
import { UnifiedItemValues } from '@/features/order/order-form/lib/unified-order-schema'
import { isInverseInstrument } from '@/lib/instrument-util'
import { Denomination, OrderSide, OrderType } from '@/types/generated/common/trading'
import { Instrument } from '@/types/generated/common/types'
import { OrderPreviewRequest } from '@/types/generated/public_api'

export type { PriceAnchor }

export interface BuildOrderPreviewRequestArgs {
  item: UnifiedItemValues | undefined
  /** The order form's global price field (`form.watch('price')`), as a string.
   * Holds an absolute price when `priceAnchor === 'price'`, otherwise holds
   * the offset (in `priceOffsetUnit`) from the anchor. */
  formPrice: string
  /** The order form's global priceAnchor field (`form.watch('priceAnchor')`). */
  priceAnchor: PriceAnchor
  /** The order form's global priceOffsetUnit field
   * (`form.watch('priceOffsetUnit')`) — only meaningful when priceAnchor is
   * relative. Defaults to 'quote_value' (matches the schema default). */
  priceOffsetUnit?: OffsetUnit
  isMarketOrder: boolean
  /** Limit Chase has no fixed price (it continuously re-prices at the best bid/ask), so it's
   * previewed like a market order rather than sent as a priceless LIMIT request. */
  isLimitChaseMode: boolean
  accountId: string
  instruments: Instrument[]
  /** This row's own live best bid/ask — used to resolve a relative anchor
   * (mid/best_bid/best_ask) into the same absolute price the "Estimated
   * price" hint shows the user, via computeEstimatedPrice. Undefined (no
   * ticker yet) falls back to omitting price, same as before this existed. */
  ticker?: AnchorPrice
  /** This row's own tick size, for rounding the resolved price — see
   * computeEstimatedPrice. */
  tickSize?: number
}

/**
 * Single source of truth for "is this row previewable" — no symbol or no
 * resolved account drops the row rather than failing the whole (single- or
 * batch-) preview request. Both buildOrderPreviewRequest below and
 * mapItemsToBatchRowIndex (see build-batch-order-preview-request.ts) call
 * this, so the two conditions can't drift apart — previously kept in sync
 * only by a comment.
 *
 * Deliberately does NOT require a positive amount — a row with no amount
 * typed yet is still previewed (see resolvePreviewAmount's placeholder
 * fallback below) so its max-size hint (order-row.tsx's maxSize prop) has
 * something to show before the user types anything, instead of staying
 * blank until the first keystroke.
 */
export function isRowPreviewable(
  item: UnifiedItemValues | undefined,
  resolvedAccountId: string
): boolean {
  return !!(item && item.symbol && resolvedAccountId)
}

/**
 * The quantity actually sent to the preview endpoint — the typed amount when
 * positive, else a nominal placeholder (the instrument's own minTradeAmount,
 * falling back to 1) purely so the backend has something to compute a
 * maxSize/fee/cost preview against. Every other field in the returned
 * preview (fees, cost, orderValueBase/Quote) is therefore computed against
 * this placeholder too when the user hasn't typed an amount yet — accepted
 * tradeoff so the max-size hint doesn't require a keystroke first.
 */
function hasTypedAmount(item: UnifiedItemValues): boolean {
  const typed = parseFormattedNumber(item.amount ?? '')
  return Number.isFinite(typed) && typed > 0
}

function resolvePreviewAmount(item: UnifiedItemValues, instruments: Instrument[]): number {
  if (hasTypedAmount(item)) return parseFormattedNumber(item.amount ?? '')

  const minTradeAmount = Number(instruments.find((i) => i.symbol === item.symbol)?.minTradeAmount)
  return Number.isFinite(minTradeAmount) && minTradeAmount > 0 ? minTradeAmount : 1
}

/**
 * The unit the placeholder quantity is in: minTradeAmount's own unit — quote for an inverse
 * contract (BTC-PERPETUAL: 10 USD), base for linear and options — NOT the row's selected
 * amountUnit. Sending it under the row's unit turned the inverse placeholder "10" into 10 BTC
 * whenever the field was on BTC, and the empty ticket previewed an $837k order.
 */
function placeholderDenomination(item: UnifiedItemValues, instruments: Instrument[]): Denomination {
  if (item.itemType === 'option') return Denomination.DENOMINATION_BASE
  const instrument = instruments.find((i) => i.symbol === item.symbol)
  return isInverseInstrument(item.symbol, instrument)
    ? Denomination.DENOMINATION_QUOTE
    : Denomination.DENOMINATION_BASE
}

/**
 * Builds an OrderPreviewRequest for the primary (first) order-form leg.
 * OrderPreviewRequest has no relative-price field, so a relative anchor
 * (mid/best_bid/best_ask) resolves to an absolute price on the client (via
 * this row's own `ticker`/`tickSize`, same math the "Estimated price" UI
 * hint uses) rather than sending the raw offset as if it were an absolute
 * price. When no ticker is available yet, price is omitted and the backend
 * falls back to the live mark, same as before this resolution existed.
 */
export function buildOrderPreviewRequest(
  args: BuildOrderPreviewRequestArgs
): OrderPreviewRequest | null {
  const {
    item,
    formPrice,
    priceAnchor,
    priceOffsetUnit,
    isMarketOrder,
    isLimitChaseMode,
    accountId,
    instruments,
    ticker,
    tickSize,
  } = args
  if (!item || !isRowPreviewable(item, accountId)) return null
  const amount = resolvePreviewAmount(item, instruments)

  // OrderPreviewRequest.price is required whenever orderType is LIMIT (see its proto comment) —
  // Limit Chase must be sent as MARKET, same as an outright market order, or the backend 400s
  // with "price is required for limit orders" on every keystroke in the amount field.
  const isMarketPriced = isMarketOrder || isLimitChaseMode
  const isRelativeLimit = !isMarketPriced && priceAnchor !== 'price'
  // formPrice is the raw form field value, which can carry comma thousand-separators
  // (e.g. syncPrice's default pre-fill formats it as "65,432.10" — see
  // formatNumberWithPrecision). The backend parses price with Rust's plain
  // `str::parse::<f64>`, which rejects commas outright and 400s with "price is
  // required for limit orders" even though a price is very much present. Strip
  // them the same way getRawValues() already does for the real submission path.
  const parsedPrice = parseFormattedNumber(formPrice || '')
  const cleanPrice =
    Number.isFinite(parsedPrice) && parsedPrice > 0 ? parsedPrice.toString() : undefined
  // For a relative anchor, formPrice holds the OFFSET (not an absolute price)
  // — resolve it against this row's own live ticker into the same absolute
  // price the UI's "Estimated price" hint shows.
  const resolvedRelativePrice = isRelativeLimit
    ? computeEstimatedPrice(
        priceAnchor,
        Number.isFinite(parsedPrice) ? parsedPrice : 0,
        priceOffsetUnit ?? 'quote_value',
        ticker,
        tickSize
      )
    : null
  const effectiveAmount = resolveEffectiveAmount(instruments, item.symbol, item.itemType, amount)
  const denomination = hasTypedAmount(item)
    ? resolveDenomination(instruments, item.symbol, item.itemType, item.amountUnit, {
        baseCurrency: item.baseCurrency,
      } as any)
    : placeholderDenomination(item, instruments)

  return {
    accountId,
    symbol: item.symbol,
    side: item.side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
    quantity: effectiveAmount.toString(),
    orderType: isMarketPriced ? OrderType.ORDER_TYPE_MARKET : OrderType.ORDER_TYPE_LIMIT,
    price: isMarketPriced
      ? undefined
      : isRelativeLimit
        ? (resolvedRelativePrice?.toString() ?? undefined)
        : cleanPrice,
    denomination,
  }
}
